import { useCallback, useEffect } from 'react'

import { Loading } from 'app/common/templates-next/page-template'
import { CompensationSettings } from 'app/dataTypes/subsidy/settings'

import { CategoryCompensationSettingsForm } from './CategoryCompensationSettingsForm'
import { extractCompensationAccrualFor } from './extractCompensationAccrualFor'
import { extractCompensationAccruals } from './extractCompensationAccruals'
import { useCategorySettingsSubmit } from '../useCategorySettingsSubmit'
import { useFormPortal } from '../useFormPortal'
import { useCompensationErrors } from './useCompensationErrors'
import extractCompensationInclude from './extractCompensationInclude'
import extractCompensationAdditional from './extractCompensationAdditional'
import extractCompensationAccrualPeriod from './extractCompensationAccrualPeriod'
import { useFormModify } from '../useFormModify'

type Props = {
  ID: string
  orgID: string
  settings: CompensationSettings
  loading?: boolean
  onDataUpdate: () => void
  onCreateFormID: (ID: string) => string
  updateSize?: () => void
  onSuccess?: () => void
  onPending?: (pending: boolean) => void
  onModify?: () => void
}

export const CategoryCompensationSettings = ({
  ID,
  orgID,
  settings,
  loading,
  onDataUpdate,
  onCreateFormID,
  updateSize,
  onSuccess,
  onPending,
  onModify,
}: Props) => {
  const { accrualsShiftsAmount } = settings
  const formID = onCreateFormID(ID)

  const { errors, checkErrors, resetErrors } = useCompensationErrors(accrualsShiftsAmount)

  const extractData = useCallback(
    (formData: FormData) => {
      const accruals = extractCompensationAccruals(formData, accrualsShiftsAmount)
      const accrualsFor = extractCompensationAccrualFor(formData)
      const include = extractCompensationInclude(formData)
      const homeschoolingOnly = extractCompensationAdditional(formData)
      const accrualPeriod = extractCompensationAccrualPeriod(formData)

      return {
        app: 'compensation' as const,
        accrualsShiftsAmount,
        accruals,
        accrualsFor,
        include,
        homeschoolingOnly,
        accrualPeriod,
      }
    },
    [accrualsShiftsAmount],
  )

  const { submit, submitting } = useCategorySettingsSubmit({
    allowanceID: ID,
    orgID,
    formID,
    onSuccess,
    onPending,
    onCheckErrors: checkErrors,
    onExtractData: extractData,
  })

  useEffect(() => {
    onDataUpdate()
  }, [settings]) // eslint-disable-line react-hooks/exhaustive-deps

  const { formPortal } = useFormPortal(formID, submit)
  useFormModify(formID, onModify)

  return (
    <div>
      {loading ? (
        <Loading />
      ) : (
        <>
          <CategoryCompensationSettingsForm
            ID={ID}
            orgID={orgID}
            formID={formID}
            settings={settings}
            disabled={submitting}
            errors={errors}
            resetErrors={resetErrors}
            updateSize={updateSize}
          />
          {formPortal}
        </>
      )}
    </div>
  )
}
